// Frontend mirrors of the backend response shapes. The backend is the
// source of truth (apps/web/src/lib/queries.ts); this file restates the
// shapes the SPA actually consumes so the data layer can type-check
// without importing server-only modules.

export interface RuntimeStatus {
  ok: boolean;
  service: string;
  public_url: string;
  database: {
    ok: boolean;
    checked_at: string | null;
  };
}

export interface GhlStatusRow {
  auth_mode: string;
  auth_status: string;
  count: number;
}

export interface GhlCredentialRow {
  credential_scope: string;
  status: string;
  count: number;
}

export interface GhlAuthEvent {
  event_type: string;
  status: string;
  occurred_at: string;
}

export interface ResolverSummary {
  routing_sites: number;
  routing_ready_sites: number;
  credential_ready_sites: number;
  write_ready_sites: number;
  fallback_write_ready_sites: number;
  oauth_metadata_ready_sites: number;
  oauth_write_ready_sites: number;
  degraded_sites: number;
  zero_touch_gate: "unproven" | "not_configured";
}

export interface GhlStatus {
  ok: boolean;
  oauth_configured: boolean;
  resolver: ResolverSummary;
  installations: Record<string, number>;
  credentials: GhlCredentialRow[];
  sites: GhlStatusRow[];
  recent_events: GhlAuthEvent[];
}

export interface SiteOauth {
  installation_id: string | null;
  company_id: string | null;
  installation_status: string | null;
  installed_at: string | null;
  uninstalled_at: string | null;
  last_seen_at: string | null;
  credential_scope: string | null;
  credential_status: string | null;
  credential_stored: boolean;
  refresh_credential_stored: boolean;
  expires_at: string | null;
  refresh_expires_at: string | null;
  last_refreshed_at: string | null;
}

export interface SiteResolverState {
  lane: string;
  state: string;
  routing_ready: boolean;
  credential_ready: boolean;
  write_ready: boolean;
  detail: string;
  last_checked_at: string | null;
  last_failure: string | null;
}

export interface Site {
  id: string;
  tenant_id: string | null;
  site_id: string;
  kind: "ghl" | "webhook";
  location_id: string | null;
  location_id_masked?: string;
  api_base_url: string;
  version_header: string;
  webhook_url: string | null;
  webhook_secret_present: boolean;
  is_active: boolean;
  auth_mode: string;
  auth_status: string;
  resolver: SiteResolverState;
  created_at: string | null;
  updated_at: string | null;
  oauth: SiteOauth;
}

export interface SiteInventory {
  sites: Site[];
}

export interface IngestProcessingEvent {
  id: string;
  occurred_at: string;
  topic: string;
  partition: number;
  offset: number;
  submitter_id: string | null;
  tenant_id: string | null;
  type: string | null;
  schema_version: string | null;
  outcome: string;
  action: string;
  reason_code: string | null;
  detail: string | null;
}

export interface IngestSubmitter {
  submitter_id: string;
  status: string;
  rate_limit_tier: string;
  tenant_id: string | null;
}

export interface IngestStatus {
  ok: boolean;
  recent_events: IngestProcessingEvent[];
  submitters: IngestSubmitter[];
}

export interface ConnectionCheckItem {
  name: string;
  ok: boolean;
  detail: string;
}

export interface ConnectionCheck {
  ok: boolean;
  checked_at: string;
  checks: ConnectionCheckItem[];
  summary: {
    active_installations: number;
    active_oauth_sites: number;
    fallback_sites: number;
    credential_records: number;
    routing_ready_sites: number;
    resolver_ready_sites: number;
    write_ready_sites: number;
    degraded_sites: number;
  };
}

export type DestinationKind = "ghl" | "webhook";

export type AddCustomerRequest =
  | {
      tenant_id: string;
      submitter_id: string;
      submitter_secret: string;
      rate_limit_tier?: string;
      destination_kind: "ghl";
      destination_handle: string;
      ghl: {
        location_id: string;
        access_token: string;
        scopes: string[];
        api_base_url: string;
        version_header: string;
      };
    }
  | {
      tenant_id: string;
      submitter_id: string;
      submitter_secret: string;
      rate_limit_tier?: string;
      destination_kind: "webhook";
      destination_handle: string;
      webhook: {
        url: string;
        secret: string;
      };
    };

export interface AddCustomerResult {
  ok: boolean;
  customer: {
    tenant_id: string;
    submitter_id: string;
    destination_handle: string;
    destination_kind: DestinationKind;
    rate_limit_tier: string;
  };
  created: {
    submitter: boolean;
    destination: boolean;
  };
  verify: {
    status: "verified" | "unverified";
    kind: DestinationKind;
    reason: string | null;
    http_status: number | null;
    checked_at: string;
  };
}

export interface ApiAuthConfig {
  ok: true;
  clerk_publishable_key: string;
  clerk_sign_in_url: string;
  clerk_after_sign_out_url: string;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly endpoint: string;
  readonly code: string | undefined;
  constructor(
    endpoint: string,
    status: number,
    message: string,
    code?: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
    this.endpoint = endpoint;
    this.status = status;
    this.code = code;
  }
}
